import { useState } from 'preact/hooks'
import { contactDetailApi } from '@/api/contactDetail'
import type { LeadQuizAttemptDto, LeadQuizDimensionDto, LeadQuizResultDto } from '@/api/leads'
import { BRAND_CLASSES } from '@/constants/theme'
import { useToast } from '@/hooks/useToast'
import { Icon } from '../contactShared'
import { formatQuizTimestamp, groupQuizAnswers, quizTierFootnote } from '../contactFormat'
import { httpsUrlOrThrow } from '@/utils/externalUrl'

interface ReportScope {
  workspaceId: string
  campaignId: string
  leadId: string
  webId: string
}

interface Props extends ReportScope {
  quiz: LeadQuizResultDto
}

const ICON_DOWNLOAD = 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4|m7 10 5 5 5-5|M12 15V3'
const ICON_CLOCK = 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20|M12 6v6l4 2'
const ICON_SWAP = 'm16 3 4 4-4 4|M20 7H4|m8 21-4-4 4-4|M4 17h16'

const TIER_CLASSES: Record<string, string> = {
  Low: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  Mid: 'bg-slate-200/70 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
  High: BRAND_CLASSES.STATUS.SUCCESS,
}

// The lead's quiz on this web (ORB-915): score and level, then every answer under its dimension with the
// dimension's average and tier, then any earlier attempts. While the answers are awaited it is a single line.
export function ContactQuizTab({ quiz, workspaceId, campaignId, leadId, webId }: Props) {
  if (quiz.Status !== 'Completed') {
    return (
      <div class="px-6 py-6">
        <p class="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
          <Icon path={ICON_CLOCK} size={15} class="text-slate-400 dark:text-slate-500" />
          <span>
            <span class="font-medium text-slate-800 dark:text-slate-200">Awaiting answers</span> · details given {formatQuizTimestamp(quiz.DetailsGivenAt, false)}
          </span>
        </p>
      </div>
    )
  }

  const scope = { workspaceId, campaignId, leadId, webId }
  const attemptCount = quiz.EarlierAttempts.length + 1
  const context = [
    quiz.QuizTitle,
    quiz.QuizVersion != null ? `Quiz version ${quiz.QuizVersion}` : null,
    `Completed ${formatQuizTimestamp(quiz.CompletedAt, true)}`,
    attemptCount > 1 ? `Attempt ${attemptCount} of ${attemptCount}` : null,
  ].filter(Boolean).join(' · ')
  const reverseScoredScaleMax = quiz.ScaleMax != null && quiz.Answers.some(answer => answer.IsReverseScored) ? quiz.ScaleMax : null

  return (
    <div class="px-6 py-6">
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div class="flex flex-wrap items-center gap-x-7 gap-y-3">
            <div class="flex items-baseline gap-1.5">
              <span class="text-2xl font-semibold tabular-nums text-slate-900 dark:text-white">{quiz.Score}</span>
              <span class="text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">out of 100</span>
            </div>
            <div class="h-9 w-px bg-slate-200 dark:bg-slate-700/70" aria-hidden="true" />
            <div class="flex items-baseline gap-1.5">
              <span class="text-2xl font-semibold text-slate-900 dark:text-white">{quiz.BandName}</span>
              <span class="text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">level</span>
            </div>
          </div>
          <p class="mt-2 text-xs text-slate-500 dark:text-slate-400">{context}</p>
        </div>
        <DownloadReportButton hasReport={quiz.HasReport} scope={scope} />
      </div>

      <div class="mt-6 overflow-hidden rounded-lg border border-slate-200 dark:border-slate-800">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b border-slate-200 dark:border-slate-800 text-left text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">
              <th class="w-12 px-4 py-2.5 font-semibold">#</th>
              <th class="px-4 py-2.5 font-semibold">Question</th>
              <th class="w-36 px-4 py-2.5 font-semibold text-right">Answer, 1 to {quiz.ScaleMax}</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100 dark:divide-slate-800/70">
            {groupQuizAnswers(quiz).map(group => (
              <DimensionRows key={group.dimension.DimensionId} dimension={group.dimension} answers={group.answers} scaleMax={quiz.ScaleMax} />
            ))}
          </tbody>
        </table>
      </div>
      {quiz.TierMidFrom != null && quiz.TierHighFrom != null && (
        <p class="mt-3 text-xs text-slate-500 dark:text-slate-400">{quizTierFootnote(quiz.TierMidFrom, quiz.TierHighFrom, reverseScoredScaleMax)}</p>
      )}
      {quiz.EarlierAttempts.length > 0 && (
        <div class="mt-8">
          <h3 class="text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">Earlier attempts</h3>
          <ul class="mt-2 divide-y divide-slate-100 dark:divide-slate-800/70 rounded-lg border border-slate-200 dark:border-slate-800">
            {quiz.EarlierAttempts.map(attempt => (
              <EarlierAttemptRow key={attempt.WebFormSubmissionId} attempt={attempt} scope={scope} />
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function DimensionRows({ dimension, answers, scaleMax }: {
  dimension: LeadQuizDimensionDto
  answers: ReturnType<typeof groupQuizAnswers>[number]['answers']
  scaleMax: number | null | undefined
}) {
  return (
    <>
      <tr class="bg-slate-50 dark:bg-slate-800/40">
        <td colSpan={2} class="px-4 py-2.5">
          <span class="font-medium text-slate-900 dark:text-white">{dimension.DimensionName}</span>
          {dimension.Tier && (
            <span class={`ml-2 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${TIER_CLASSES[dimension.Tier] ?? BRAND_CLASSES.STATUS.DEFAULT}`}>
              {dimension.Tier}
            </span>
          )}
        </td>
        <td class="px-4 py-2.5 text-right font-semibold tabular-nums text-slate-900 dark:text-white">
          {dimension.Average != null ? dimension.Average.toFixed(1) : '—'}
        </td>
      </tr>
      {answers.map(({ number, answer }) => (
        <tr key={answer.QuestionId}>
          <td class="px-4 py-2.5 align-top tabular-nums text-slate-400 dark:text-slate-500">{number}</td>
          <td class="px-4 py-2.5 text-slate-800 dark:text-slate-200">
            {answer.QuestionText}
            {answer.IsReverseScored && (
              <span
                class="ml-1.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 align-[1px] text-xs font-medium bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                title={scaleMax != null ? `A higher answer is the weaker one, so it counts as ${scaleMax + 1} minus the answer` : undefined}
              >
                <Icon path={ICON_SWAP} size={12} />
                Reverse scored
              </span>
            )}
          </td>
          <td class={`px-4 py-2.5 align-top text-right tabular-nums ${answer.Value == null ? 'text-slate-500 dark:text-slate-400' : 'text-slate-800 dark:text-slate-200'}`}>
            {answer.Value ?? 'Not applicable'}
            {answer.IsReverseScored && answer.ScoredValue != null && (
              <span class="block text-xs font-normal text-slate-500 dark:text-slate-400">counts as {answer.ScoredValue}</span>
            )}
          </td>
        </tr>
      ))}
    </>
  )
}

function EarlierAttemptRow({ attempt, scope }: { attempt: LeadQuizAttemptDto; scope: ReportScope }) {
  const { opening, openReport } = useReportOpener(scope)

  return (
    <li class="flex flex-wrap items-center gap-x-6 gap-y-1 px-4 py-3 text-sm">
      <span class="w-40 tabular-nums text-slate-500 dark:text-slate-400">{formatQuizTimestamp(attempt.CompletedAt, true)}</span>
      <span class="w-28">
        <span class="font-semibold tabular-nums text-slate-900 dark:text-white">{attempt.Score}</span>{' '}
        <span class="text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">out of 100</span>
      </span>
      <span class="w-28 font-medium text-slate-800 dark:text-slate-200">{attempt.BandName}</span>
      {attempt.QuizVersion != null && (
        <span class="text-xs text-slate-500 dark:text-slate-400">Quiz version {attempt.QuizVersion}</span>
      )}
      {attempt.HasReport ? (
        <button
          type="button"
          onClick={() => openReport(attempt.WebFormSubmissionId)}
          disabled={opening}
          class="ml-auto inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 dark:text-primary-400 hover:underline disabled:opacity-60 disabled:cursor-default"
        >
          <Icon path={ICON_DOWNLOAD} size={14} />
          Report
        </button>
      ) : (
        <span class="ml-auto inline-flex items-center gap-1.5 text-sm font-medium text-slate-400 dark:text-slate-500">
          <Icon path={ICON_DOWNLOAD} size={14} />
          Preparing report
        </span>
      )}
    </li>
  )
}

function DownloadReportButton({ hasReport, scope }: { hasReport: boolean; scope: ReportScope }) {
  const { opening, openReport } = useReportOpener(scope)

  // Until the PDF exists there is nothing to open, so the button can't lead to a broken link.
  if (!hasReport) {
    return (
      <button
        type="button"
        disabled
        class="inline-flex items-center gap-1.5 rounded-md border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm font-medium text-slate-400 dark:text-slate-500 cursor-not-allowed"
      >
        <Icon path={ICON_DOWNLOAD} size={15} />
        Preparing report
      </button>
    )
  }

  return (
    <button
      type="button"
      onClick={() => openReport()}
      disabled={opening}
      class="inline-flex items-center gap-1.5 rounded-md border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-60 disabled:cursor-default"
    >
      <Icon path={ICON_DOWNLOAD} size={15} />
      Download report
    </button>
  )
}

function useReportOpener({ workspaceId, campaignId, leadId, webId }: ReportScope) {
  const { showError } = useToast()
  const [opening, setOpening] = useState(false)

  const openReport = async (webFormSubmissionId?: string) => {
    if (opening) return
    // Opened before the request, while the click still counts as the user's: a window opened after an
    // await is treated as a popup and blocked.
    const reportWindow = window.open('about:blank', '_blank')
    if (!reportWindow) {
      showError('Report not opened', 'Your browser blocked the new tab. Allow pop-ups for this site and try again.')
      return
    }
    reportWindow.opener = null
    setOpening(true)
    const res = await contactDetailApi.getQuizReportUrl(workspaceId, campaignId, leadId, webId, webFormSubmissionId)
    setOpening(false)
    if (!res.data) {
      reportWindow.close()
      showError('Report not opened', res.error || 'The report could not be opened.')
      return
    }
    try {
      reportWindow.location.href = httpsUrlOrThrow(res.data.Url)
    } catch {
      reportWindow.close()
      showError('Report not opened', 'The report link was not a secure address.')
    }
  }

  return { opening, openReport }
}
